import React, { useState, useMemo } from 'react';
import {
  Users,
  Search,
  Filter,
  Plus,
  Home,
  Phone,
  Mail,
  Car,
  CheckCircle2,
  Clock,
  AlertCircle,
  XCircle,
  FileText,
  Edit3,
  Save,
  X,
  ChevronRight,
  Shield,
  ArrowLeft,
  Printer,
  Key,
  Download,
  ChevronLeft,
  ChevronDown,
} from 'lucide-react';
import { ResidentDirectoryItem } from '../../types';
import { useApp } from '../../context/AppContext';
import { ResidentSubMenuKey } from '../ResidentDirectory';
import { ResidentUnitDetailView } from './ResidentUnitDetailView';
import { MemberPasscodePrintModal } from './MemberPasscodePrintModal';

interface Props {
  onBack: () => void;
  onNavigate?: (tab: ResidentSubMenuKey) => void;
}

export type UnitOccupancyTab =
  | 'all'
  | 'owner_occupied'
  | 'rented'
  | 'vacant'
  | 'legal_action';

export interface UnitTableRow {
  id: string;
  orderNo: number;
  roomNumber: string;
  unitDeedNumber: string;
  ownerName: string;
  nationality: string;
  propertyType: string;
  areaSqw: number;
  ownershipPercent: string;
  ownershipRatio: string;
  floors: number;
  status: 'อยู่อาศัยเอง' | 'ปล่อยเช่า' | 'ห้องว่าง' | 'ถูกดำเนินการตามกฎหมาย';
  residentCount: number;
  usesApp: boolean;
  houseNo?: string;
  moo?: string;
  villageBuilding?: string;
  road?: string;
  soi?: string;
  subDistrict?: string;
}

export const ResidentMembersView: React.FC<Props> = ({ onBack, onNavigate }) => {
  const { residents } = useApp();

  // Active unit detail view state
  const [selectedRoomNumber, setSelectedRoomNumber] = useState<string | null>(null);

  // Tabs and filters state
  const [occupancyTab, setOccupancyTab] = useState<UnitOccupancyTab>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeDropdownFilter, setTypeDropdownFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showGenCodeModal, setShowGenCodeModal] = useState(false);
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const [showPrintSuccessToast, setShowPrintSuccessToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // CSV Export handler matching the Excel report shown in Image 2
  const handleExportCSV = (type: 'units' | 'residents' = 'units') => {
    let csvContent = '\uFEFF'; // UTF-8 BOM for Thai Excel

    if (type === 'units') {
      const orgTitle = 'นิติบุคคลหมู่บ้านจัดสรร เอส วี เอ็ม ซัพพอร์ต';
      const reportTitle = 'รายงานลูกบ้าน - ทะเบียนลูกบ้าน';
      const statusFilter = `เรียงตามสถานะ - ${
        occupancyTab === 'all'
          ? 'ทั้งหมด'
          : occupancyTab === 'owner_occupied'
          ? 'อยู่อาศัยเอง'
          : occupancyTab === 'rented'
          ? 'ปล่อยเช่า'
          : occupancyTab === 'vacant'
          ? 'ห้องว่าง'
          : 'ถูกดำเนินการตามกฎหมาย'
      }`;
      const appFilter = 'เรียงตามการดาวน์โหลดแอพ - ทั้งหมด';

      // Rows 1-4
      csvContent += `"${orgTitle}"\n`;
      csvContent += `"${reportTitle}"\n`;
      csvContent += `"${statusFilter}"\n`;
      csvContent += `"${appFilter}"\n\n`;

      // Row 6 (Headers) matching the Excel screenshot
      const headers = [
        'เลขที่ห้อง',
        'ห้องชุดเลขที่',
        'ประเภทแบบบ้าน/ห้องชุด',
        'ชั้น',
        'ขนาดพื้นที่',
        'เลขที่',
        'หมู่ที่',
        'หมู่บ้าน/อาคาร',
        'ถนน',
        'ซอย',
        'แขวง/ตำบล',
      ];
      csvContent += headers.map((h) => `"${h}"`).join(',') + '\n';

      // Data Rows
      filteredUnits.forEach((u) => {
        const row = [
          u.roomNumber,
          u.unitDeedNumber === '-' ? '' : u.unitDeedNumber,
          u.propertyType,
          u.floors ? u.floors.toString() : '',
          u.areaSqw ? u.areaSqw.toFixed(2) : '',
          u.houseNo || '',
          u.moo || '',
          u.villageBuilding || '',
          u.road || '',
          u.soi || '',
          u.subDistrict || '',
        ];
        csvContent += row.map((val) => `"${(val || '').replace(/"/g, '""')}"`).join(',') + '\n';
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `รายงานลูกบ้าน_ทะเบียนลูกบ้าน.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setToastMessage('ดาวน์โหลดไฟล์ CSV (รายงานลูกบ้าน - ทะเบียนลูกบ้าน.csv) เรียบร้อยแล้ว');
    } else {
      // Data for "ข้อมูลผู้อยู่อาศัย" (Sheet 2)
      const orgTitle = 'นิติบุคคลหมู่บ้านจัดสรร เอส วี เอ็ม ซัพพอร์ต';
      const reportTitle = 'รายงานลูกบ้าน - ข้อมูลผู้อยู่อาศัย';
      csvContent += `"${orgTitle}"\n`;
      csvContent += `"${reportTitle}"\n\n`;

      const headers = [
        'เลขที่ห้อง',
        'ชื่อ-นามสกุลเจ้าของ/ผู้อยู่อาศัย',
        'สถานะการอยู่อาศัย',
        'ประเภทที่พัก',
        'สัญชาติ',
        'ขนาดพื้นที่ (ตร.ว.)',
        'สัดส่วนกรรมสิทธิ์',
        'การใช้งานแอปพลิเคชัน',
        'ที่อยู่/สถานที่ตั้ง',
      ];
      csvContent += headers.map((h) => `"${h}"`).join(',') + '\n';

      filteredUnits.forEach((u) => {
        const fullAddr = [u.houseNo && `เลขที่ ${u.houseNo}`, u.moo && `หมู่ ${u.moo}`, u.villageBuilding, u.road && `ถ.${u.road}`, u.soi && `ซ.${u.soi}`, u.subDistrict && `แขวง/ต.${u.subDistrict}`].filter(Boolean).join(' ');
        const row = [
          u.roomNumber,
          u.ownerName,
          u.status,
          u.propertyType,
          u.nationality,
          u.areaSqw.toFixed(2),
          u.ownershipRatio,
          u.usesApp ? 'ใช้แอปพลิเคชัน' : 'ยังไม่ใช้แอปพลิเคชัน',
          fullAddr || '-',
        ];
        csvContent += row.map((val) => `"${(val || '').replace(/"/g, '""')}"`).join(',') + '\n';
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `รายงานลูกบ้าน_ข้อมูลผู้อยู่อาศัย.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setToastMessage('ดาวน์โหลดไฟล์ CSV (รายงานลูกบ้าน - ข้อมูลผู้อยู่อาศัย.csv) เรียบร้อยแล้ว');
    }

    setShowPrintMenu(false);
    setShowPrintSuccessToast(true);
    setTimeout(() => setShowPrintSuccessToast(false), 4000);
  };

  const tabs: { key: ResidentSubMenuKey; label: string }[] = [
    { key: 'members', label: 'เจ้าของร่วม/สมาชิก' },
    { key: 'vehicles', label: 'ยานพาหนะ' },
    { key: 'keycards', label: 'คีย์การ์ด' },
    { key: 'foreign_ownership', label: 'กรรมสิทธิ์ชาวต่างชาติ' },
    { key: 'pets', label: 'สัตว์เลี้ยง' },
    { key: 'title_deeds', label: 'กรรมสิทธิ์' },
    { key: 'history_logs', label: 'ประวัติผู้อยู่อาศัย' },
  ];

  // Comprehensive table dataset representing the units (matching images 9, 10, 22, 23 and excel export image)
  const initialUnits: UnitTableRow[] = useMemo(() => {
    return [
      {
        id: 'u-1',
        orderNo: 1,
        roomNumber: '1086/01',
        unitDeedNumber: '-',
        ownerName: 'Mr.adisorn kaeophakdee',
        nationality: 'AF',
        propertyType: 'บ้านเดี่ยว',
        areaSqw: 125.3,
        ownershipPercent: '0.85%',
        ownershipRatio: '1408.7',
        floors: 18,
        status: 'ปล่อยเช่า',
        residentCount: 9,
        usesApp: true,
        houseNo: '8558',
        moo: '9',
        villageBuilding: 'หมู่ที่ 5',
        road: 'Jareonkrung',
        soi: '9',
        subDistrict: 'สุขุมวิท',
      },
      {
        id: 'u-2',
        orderNo: 2,
        roomNumber: '1086/02',
        unitDeedNumber: '-',
        ownerName: 'คุณวีรวิช ทิพยชัยวรุตม์',
        nationality: 'TH',
        propertyType: '4 ห้องนอน',
        areaSqw: 150.35,
        ownershipPercent: '0.88%',
        ownershipRatio: '1450.0',
        floors: 2,
        status: 'อยู่อาศัยเอง',
        residentCount: 3,
        usesApp: true,
        houseNo: '183/168',
        moo: '10',
        villageBuilding: 'อาคารB',
        road: 'บางนา',
        soi: 'บางนา',
        subDistrict: 'สุขุมวิท',
      },
      {
        id: 'u-3',
        orderNo: 3,
        roomNumber: '1086/03',
        unitDeedNumber: 'A-103',
        ownerName: 'คุณPatnarin Korboon',
        nationality: 'SE',
        propertyType: '3 ห้องนอน',
        areaSqw: 122.0,
        ownershipPercent: '0.75%',
        ownershipRatio: '1240.2',
        floors: 3,
        status: 'อยู่อาศัยเอง',
        residentCount: 2,
        usesApp: true,
        houseNo: '1086/03',
        moo: '9',
        villageBuilding: 'อาคารA',
        road: 'สุขุมวิท',
        soi: 'สุขุมวิท 114',
        subDistrict: 'บางจาก',
      },
      {
        id: 'u-4',
        orderNo: 4,
        roomNumber: '1086/04',
        unitDeedNumber: '-',
        ownerName: 'คุณจอร์แดน แอนเดอร์สัน',
        nationality: 'US',
        propertyType: 'ดูเพล็กซ์',
        areaSqw: 175.88,
        ownershipPercent: '0.98%',
        ownershipRatio: '1620.0',
        floors: 4,
        status: 'ปล่อยเช่า',
        residentCount: 4,
        usesApp: false,
        houseNo: '1086/04',
        moo: '',
        villageBuilding: '',
        road: 'เจริญกรุง',
        soi: '',
        subDistrict: 'บางรัก',
      },
      {
        id: 'u-5',
        orderNo: 5,
        roomNumber: '1086/05',
        unitDeedNumber: 'B-201',
        ownerName: 'บริษัท สยาม แคปิตอล จำกัด',
        nationality: 'TH',
        propertyType: 'ดูเพล็กซ์',
        areaSqw: 200.32,
        ownershipPercent: '0.58%',
        ownershipRatio: '950.0',
        floors: 5,
        status: 'ห้องว่าง',
        residentCount: 0,
        usesApp: false,
        houseNo: '1086/05',
        moo: '',
        villageBuilding: '',
        road: 'เจริญกรุง',
        soi: '',
        subDistrict: 'บางรัก',
      },
      {
        id: 'u-6',
        orderNo: 6,
        roomNumber: '1086/06',
        unitDeedNumber: '-',
        ownerName: 'นายสุรชัย วงศ์วานิช',
        nationality: 'TH',
        propertyType: '4 ห้องนอน',
        areaSqw: 180.0,
        ownershipPercent: '0.81%',
        ownershipRatio: '1340.0',
        floors: 6,
        status: 'ถูกดำเนินการตามกฎหมาย',
        residentCount: 1,
        usesApp: false,
        houseNo: '1086/06',
        moo: '',
        villageBuilding: '',
        road: 'เจริญกรุง',
        soi: '',
        subDistrict: 'บางรัก',
      },
      {
        id: 'u-7',
        orderNo: 7,
        roomNumber: '1086/07',
        unitDeedNumber: 'A-107',
        ownerName: 'Mrs. Mei Ling Zhang',
        nationality: 'CH',
        propertyType: '3 ห้องนอน',
        areaSqw: 130.72,
        ownershipPercent: '0.91%',
        ownershipRatio: '1510.0',
        floors: 7,
        status: 'ปล่อยเช่า',
        residentCount: 3,
        usesApp: true,
        houseNo: '1086/07',
        moo: '',
        villageBuilding: '',
        road: 'เจริญกรุง',
        soi: '',
        subDistrict: 'บางรัก',
      },
      {
        id: 'u-8',
        orderNo: 8,
        roomNumber: '1086/08',
        unitDeedNumber: '-',
        ownerName: 'น.ส. ศิริพร กิจเจริญ',
        nationality: 'TH',
        propertyType: 'ดูเพล็กซ์',
        areaSqw: 193.93,
        ownershipPercent: '0.78%',
        ownershipRatio: '1290.0',
        floors: 8,
        status: 'อยู่อาศัยเอง',
        residentCount: 2,
        usesApp: true,
        houseNo: '1086/08',
        moo: '',
        villageBuilding: '',
        road: 'เจริญกรุง',
        soi: '',
        subDistrict: 'บางรัก',
      },
      {
        id: 'u-9',
        orderNo: 9,
        roomNumber: '1086/09',
        unitDeedNumber: '-',
        ownerName: 'Mr. David Miller',
        nationality: 'GB',
        propertyType: '4 ห้องนอน',
        areaSqw: 169.0,
        ownershipPercent: '0.86%',
        ownershipRatio: '1430.0',
        floors: 9,
        status: 'อยู่อาศัยเอง',
        residentCount: 2,
        usesApp: true,
        houseNo: '1086/09',
        moo: '',
        villageBuilding: '',
        road: 'เจริญกรุง',
        soi: '',
        subDistrict: 'บางรัก',
      },
      {
        id: 'u-10',
        orderNo: 10,
        roomNumber: '1086/10',
        unitDeedNumber: 'C-302',
        ownerName: 'นายพงษ์ศักดิ์ ธรรมรัตน์',
        nationality: 'TH',
        propertyType: 'ดูเพล็กซ์',
        areaSqw: 252.0,
        ownershipPercent: '0.82%',
        ownershipRatio: '1360.0',
        floors: 10,
        status: 'อยู่อาศัยเอง',
        residentCount: 3,
        usesApp: true,
        houseNo: '1086/10',
        moo: '',
        villageBuilding: '',
        road: 'เจริญกรุง',
        soi: '',
        subDistrict: 'บางรัก',
      },
      {
        id: 'u-11',
        orderNo: 11,
        roomNumber: '1086/11',
        unitDeedNumber: '-',
        ownerName: 'นายธนาธิป เจริญสุข',
        nationality: 'TH',
        propertyType: 'สตูดิโอ',
        areaSqw: 40.59,
        ownershipPercent: '0.80%',
        ownershipRatio: '1320.0',
        floors: 2,
        status: 'ปล่อยเช่า',
        residentCount: 2,
        usesApp: false,
        houseNo: '1086/11',
        moo: '',
        villageBuilding: '',
        road: 'เจริญกรุง',
        soi: '',
        subDistrict: 'บางรัก',
      },
      {
        id: 'u-12',
        orderNo: 12,
        roomNumber: '1086/12',
        unitDeedNumber: '-',
        ownerName: 'นางสาวกุลธิดา ปรีดาภิรมย์',
        nationality: 'TH',
        propertyType: 'สตูดิโอ',
        areaSqw: 46.32,
        ownershipPercent: '0.85%',
        ownershipRatio: '1400.0',
        floors: 8,
        status: 'อยู่อาศัยเอง',
        residentCount: 4,
        usesApp: true,
        houseNo: '1086/12',
        moo: '',
        villageBuilding: '',
        road: 'เจริญกรุง',
        soi: '',
        subDistrict: 'บางรัก',
      },
      {
        id: 'u-13',
        orderNo: 13,
        roomNumber: '1086/13',
        unitDeedNumber: '-',
        ownerName: 'นายพิเชษฐ์ ลิ้มทองกุล',
        nationality: 'TH',
        propertyType: 'สตูดิโอ',
        areaSqw: 30.89,
        ownershipPercent: '0.70%',
        ownershipRatio: '1100.0',
        floors: 6,
        status: 'อยู่อาศัยเอง',
        residentCount: 1,
        usesApp: true,
        houseNo: '1086/13',
        moo: '',
        villageBuilding: '',
        road: 'เจริญกรุง',
        soi: '',
        subDistrict: 'บางรัก',
      },
      {
        id: 'u-14',
        orderNo: 14,
        roomNumber: '1086/14',
        unitDeedNumber: '-',
        ownerName: 'นางวรรณา รัตนไพศาล',
        nationality: 'TH',
        propertyType: 'คอนโด',
        areaSqw: 42.85,
        ownershipPercent: '0.72%',
        ownershipRatio: '1150.0',
        floors: 1,
        status: 'ปล่อยเช่า',
        residentCount: 2,
        usesApp: false,
        houseNo: '1086/14',
        moo: '',
        villageBuilding: '',
        road: '',
        soi: '',
        subDistrict: 'ลำไทร',
      },
      {
        id: 'u-15',
        orderNo: 15,
        roomNumber: '1086/15',
        unitDeedNumber: '-',
        ownerName: 'นายชวลิต รุ่งอรุณ',
        nationality: 'TH',
        propertyType: 'คอนโด',
        areaSqw: 42.86,
        ownershipPercent: '0.72%',
        ownershipRatio: '1150.0',
        floors: 1,
        status: 'อยู่อาศัยเอง',
        residentCount: 2,
        usesApp: true,
        houseNo: '1086/15',
        moo: '',
        villageBuilding: '',
        road: 'เจริญกรุง',
        soi: '',
        subDistrict: 'บางรัก',
      },
      {
        id: 'u-16',
        orderNo: 16,
        roomNumber: '1086/16',
        unitDeedNumber: '-',
        ownerName: 'นางสาวกรรณิการ์ มงคลสุข',
        nationality: 'TH',
        propertyType: 'คอนโด',
        areaSqw: 42.87,
        ownershipPercent: '0.72%',
        ownershipRatio: '1150.0',
        floors: 1,
        status: 'อยู่อาศัยเอง',
        residentCount: 1,
        usesApp: true,
        houseNo: '1086/16',
        moo: '',
        villageBuilding: '',
        road: 'เจริญกรุง',
        soi: '',
        subDistrict: 'บางรัก',
      },
      {
        id: 'u-17',
        orderNo: 17,
        roomNumber: '1086/17',
        unitDeedNumber: '-',
        ownerName: 'นายภาคภูมิ เกษมสรรค์',
        nationality: 'TH',
        propertyType: 'คอนโด',
        areaSqw: 42.88,
        ownershipPercent: '0.72%',
        ownershipRatio: '1150.0',
        floors: 1,
        status: 'ปล่อยเช่า',
        residentCount: 2,
        usesApp: false,
        houseNo: '1086/17',
        moo: '',
        villageBuilding: '',
        road: 'เจริญกรุง',
        soi: '',
        subDistrict: 'บางรัก',
      },
      {
        id: 'u-18',
        orderNo: 18,
        roomNumber: '1086/18',
        unitDeedNumber: '-',
        ownerName: 'นายศิริชัย เลิศมณี',
        nationality: 'TH',
        propertyType: 'คอนโด',
        areaSqw: 42.89,
        ownershipPercent: '0.72%',
        ownershipRatio: '1150.0',
        floors: 1,
        status: 'อยู่อาศัยเอง',
        residentCount: 3,
        usesApp: true,
        houseNo: '1,3,5,7',
        moo: '',
        villageBuilding: '',
        road: 'เจริญกรุง',
        soi: '',
        subDistrict: 'บางรัก',
      },
      {
        id: 'u-19',
        orderNo: 19,
        roomNumber: '1086/19',
        unitDeedNumber: '-',
        ownerName: 'น.ส.อารียา ปัญญาวงศ์',
        nationality: 'TH',
        propertyType: 'คอนโด',
        areaSqw: 42.90,
        ownershipPercent: '0.72%',
        ownershipRatio: '1150.0',
        floors: 1,
        status: 'อยู่อาศัยเอง',
        residentCount: 2,
        usesApp: true,
        houseNo: '1086/19',
        moo: '',
        villageBuilding: '',
        road: 'เจริญกรุง',
        soi: '',
        subDistrict: 'บางรัก',
      },
      {
        id: 'u-20',
        orderNo: 20,
        roomNumber: '1086/20',
        unitDeedNumber: '-',
        ownerName: 'นายอดุลย์ จิระเดช',
        nationality: 'TH',
        propertyType: 'คอนโด',
        areaSqw: 42.91,
        ownershipPercent: '0.72%',
        ownershipRatio: '1150.0',
        floors: 1,
        status: 'ปล่อยเช่า',
        residentCount: 1,
        usesApp: true,
        houseNo: '1086/20',
        moo: '',
        villageBuilding: '',
        road: 'เจริญกรุง',
        soi: '',
        subDistrict: 'บางรัก',
      },
      {
        id: 'u-office',
        orderNo: 21,
        roomNumber: 'สำนักงานนิติบุคคล',
        unitDeedNumber: '-',
        ownerName: 'นิติบุคคลอาคารชุด / ส่วนกลาง',
        nationality: 'TH',
        propertyType: 'ออฟฟิศสำนักงาน',
        areaSqw: 50.79,
        ownershipPercent: '0.00%',
        ownershipRatio: '0.0',
        floors: 1,
        status: 'อยู่อาศัยเอง',
        residentCount: 0,
        usesApp: true,
        houseNo: '',
        moo: '',
        villageBuilding: '',
        road: '',
        soi: '',
        subDistrict: '',
      },
      {
        id: 'u-21',
        orderNo: 22,
        roomNumber: '1086/21',
        unitDeedNumber: '-',
        ownerName: 'นายประสิทธิ์ มั่นคง',
        nationality: 'TH',
        propertyType: '3 ห้องนอน',
        areaSqw: 120.0,
        ownershipPercent: '0.80%',
        ownershipRatio: '1300.0',
        floors: 2,
        status: 'อยู่อาศัยเอง',
        residentCount: 3,
        usesApp: true,
        houseNo: '',
        moo: '',
        villageBuilding: '',
        road: '',
        soi: '',
        subDistrict: '',
      },
      {
        id: 'u-22',
        orderNo: 23,
        roomNumber: '1086/22',
        unitDeedNumber: '-',
        ownerName: 'นางสุภาพรรณ บุณยะรัตน์',
        nationality: 'TH',
        propertyType: '3 ห้องนอน',
        areaSqw: 109.97,
        ownershipPercent: '0.74%',
        ownershipRatio: '1220.0',
        floors: 2,
        status: 'ปล่อยเช่า',
        residentCount: 2,
        usesApp: true,
        houseNo: '199/1999',
        moo: '6',
        villageBuilding: 'อาคารA',
        road: 'สุขุมวิท',
        soi: 'สุขุมวิท 11',
        subDistrict: 'สำโรงเหนือ',
      },
      {
        id: 'u-23',
        orderNo: 24,
        roomNumber: '1086/23',
        unitDeedNumber: '-',
        ownerName: 'นายณัฐวุฒิ สมหวัง',
        nationality: 'TH',
        propertyType: '3 ห้องนอน',
        areaSqw: 25.7,
        ownershipPercent: '0.60%',
        ownershipRatio: '980.0',
        floors: 2,
        status: 'อยู่อาศัยเอง',
        residentCount: 2,
        usesApp: true,
        houseNo: '199/1910',
        moo: '6666',
        villageBuilding: 'อาคารA',
        road: 'สุขุมวิท',
        soi: 'สุขุมวิท 68',
        subDistrict: 'สุขุมวิท',
      },
      {
        id: 'u-25',
        orderNo: 25,
        roomNumber: '1086/25',
        unitDeedNumber: '-',
        ownerName: 'นายคมสันต์ ว่องไว',
        nationality: 'TH',
        propertyType: '3 ห้องนอน',
        areaSqw: 120.0,
        ownershipPercent: '0.80%',
        ownershipRatio: '1300.0',
        floors: 2,
        status: 'อยู่อาศัยเอง',
        residentCount: 3,
        usesApp: true,
        houseNo: '199/1955',
        moo: '75',
        villageBuilding: 'อาคารA',
        road: 'สุขุมวิท',
        soi: 'สุขุมวิท 115555',
        subDistrict: 'บางนา',
      },
      {
        id: 'u-26',
        orderNo: 26,
        roomNumber: '1086/26',
        unitDeedNumber: '-',
        ownerName: 'บจก. โชคเจริญพาณิชย์',
        nationality: 'TH',
        propertyType: 'อาคารพาณิชย์',
        areaSqw: 35.0,
        ownershipPercent: '0.65%',
        ownershipRatio: '1050.0',
        floors: 2,
        status: 'อยู่อาศัยเอง',
        residentCount: 2,
        usesApp: false,
        houseNo: '1112',
        moo: '3',
        villageBuilding: '3',
        road: '12',
        soi: '4',
        subDistrict: 'อรัญญิก',
      },
      {
        id: 'u-27',
        orderNo: 27,
        roomNumber: '1086/27',
        unitDeedNumber: '-',
        ownerName: 'นางสาวพิมพ์ใจ สุขสวัสดิ์',
        nationality: 'TH',
        propertyType: '3 ห้องนอน',
        areaSqw: 119.98,
        ownershipPercent: '0.80%',
        ownershipRatio: '1300.0',
        floors: 2,
        status: 'อยู่อาศัยเอง',
        residentCount: 3,
        usesApp: true,
        houseNo: '199/1977',
        moo: '9',
        villageBuilding: 'อาคารA',
        road: 'สุขุมวิท',
        soi: 'สุขุมวิท 113',
        subDistrict: 'สำโรงเหนือ',
      },
      {
        id: 'u-40',
        orderNo: 28,
        roomNumber: '1086/40',
        unitDeedNumber: '-',
        ownerName: 'นายพงศ์สิทธิ์ คำมี',
        nationality: 'TH',
        propertyType: '3 ห้องนอน',
        areaSqw: 112.0,
        ownershipPercent: '0.76%',
        ownershipRatio: '1250.0',
        floors: 2,
        status: 'ปล่อยเช่า',
        residentCount: 2,
        usesApp: true,
        houseNo: '199/1999',
        moo: '9',
        villageBuilding: 'ศรีสม',
        road: 'สุขุมวิท113',
        soi: 'สุขุมวิท',
        subDistrict: 'อรัญญิก',
      },
      {
        id: 'u-301-1',
        orderNo: 29,
        roomNumber: '301/001',
        unitDeedNumber: '-',
        ownerName: 'นายทวีศักดิ์ ธรรมนูญ',
        nationality: 'TH',
        propertyType: 'ทาวน์โฮม',
        areaSqw: 34.9,
        ownershipPercent: '0.62%',
        ownershipRatio: '990.0',
        floors: 2,
        status: 'อยู่อาศัยเอง',
        residentCount: 3,
        usesApp: true,
        houseNo: '301/1',
        moo: '',
        villageBuilding: 'อารียา เดอะ คัลเลอร์ส',
        road: 'วงแหวน - รามอินทรา2',
        soi: 'สุเหร่าคลองหนึ่ง15',
        subDistrict: 'บางชัน',
      },
      {
        id: 'u-301-2',
        orderNo: 30,
        roomNumber: '301/002',
        unitDeedNumber: '-',
        ownerName: 'นายสมพร ชัยวัฒน์',
        nationality: 'TH',
        propertyType: 'ทาวน์โฮม',
        areaSqw: 25.7,
        ownershipPercent: '0.55%',
        ownershipRatio: '880.0',
        floors: 2,
        status: 'ปล่อยเช่า',
        residentCount: 2,
        usesApp: false,
        houseNo: '301/2',
        moo: '',
        villageBuilding: 'อารียา เดอะ คัลเลอร์ส',
        road: 'วงแหวน - รามอินทรา2',
        soi: 'สุเหร่าคลองหนึ่ง15',
        subDistrict: 'บางชัน',
      },
      {
        id: 'u-41',
        orderNo: 31,
        roomNumber: '1086/41',
        unitDeedNumber: '-',
        ownerName: 'นางสาวนฤมล พรประเสริฐ',
        nationality: 'TH',
        propertyType: '3 ห้องนอน',
        areaSqw: 110.0,
        ownershipPercent: '0.75%',
        ownershipRatio: '1240.0',
        floors: 2,
        status: 'อยู่อาศัยเอง',
        residentCount: 2,
        usesApp: true,
        houseNo: '199/194',
        moo: '7',
        villageBuilding: 'อาคารA',
        road: 'สุขุมวิท',
        soi: 'สุขุมวิท 68',
        subDistrict: 'บางนา',
      },
      {
        id: 'u-301-3',
        orderNo: 32,
        roomNumber: '301/003',
        unitDeedNumber: '-',
        ownerName: 'นายเกียรติศักดิ์ เสนาเมือง',
        nationality: 'TH',
        propertyType: 'ทาวน์โฮม',
        areaSqw: 25.7,
        ownershipPercent: '0.55%',
        ownershipRatio: '880.0',
        floors: 2,
        status: 'อยู่อาศัยเอง',
        residentCount: 1,
        usesApp: true,
        houseNo: '301/3',
        moo: '',
        villageBuilding: 'อารียา เดอะ คัลเลอร์ส',
        road: 'วงแหวน - รามอินทรา2',
        soi: 'สุเหร่าคลองหนึ่ง15',
        subDistrict: 'บางชัน',
      },
      {
        id: 'u-134',
        orderNo: 33,
        roomNumber: '1086/134',
        unitDeedNumber: '-',
        ownerName: 'นายชานนท์ สันติสุข',
        nationality: 'TH',
        propertyType: '1 ห้องนอน',
        areaSqw: 45.0,
        ownershipPercent: '0.70%',
        ownershipRatio: '1120.0',
        floors: 8,
        status: 'อยู่อาศัยเอง',
        residentCount: 2,
        usesApp: true,
        houseNo: '',
        moo: '',
        villageBuilding: '',
        road: '',
        soi: '',
        subDistrict: '',
      },
    ];
  }, []);

  // Filter units
  const filteredUnits = useMemo(() => {
    return initialUnits.filter((u) => {
      // Occupancy status filter
      if (occupancyTab === 'owner_occupied' && u.status !== 'อยู่อาศัยเอง') return false;
      if (occupancyTab === 'rented' && u.status !== 'ปล่อยเช่า') return false;
      if (occupancyTab === 'vacant' && u.status !== 'ห้องว่าง') return false;
      if (occupancyTab === 'legal_action' && u.status !== 'ถูกดำเนินการตามกฎหมาย') return false;

      // Dropdown filter
      if (typeDropdownFilter !== 'all' && u.propertyType !== typeDropdownFilter) return false;

      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const match =
          u.roomNumber.toLowerCase().includes(q) ||
          u.ownerName.toLowerCase().includes(q) ||
          u.nationality.toLowerCase().includes(q) ||
          u.propertyType.toLowerCase().includes(q);
        if (!match) return false;
      }

      return true;
    });
  }, [initialUnits, occupancyTab, typeDropdownFilter, searchQuery]);

  // Pagination calculation
  const totalItems = 117; // matching image total
  const displayedUnits = filteredUnits.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // App usage counts (matching UI badges)
  const appActiveCount = 59;
  const appInactiveCount = 58;

  // If a room is clicked, render the comprehensive unit detail view (images 11-18)
  if (selectedRoomNumber) {
    return (
      <ResidentUnitDetailView
        roomNumber={selectedRoomNumber}
        onBack={() => setSelectedRoomNumber(null)}
      />
    );
  }

  return (
    <div className="font-prompt bg-white min-h-[85vh] rounded-2xl shadow-xs border border-slate-200/80 overflow-hidden">
      {/* 1. Top Sub-Navigation Tabs */}
      <div className="border-b border-slate-200 bg-slate-50/50 px-4 sm:px-6 pt-3 overflow-x-auto">
        <div className="flex items-center gap-6 sm:gap-8 min-w-max text-xs sm:text-sm">
          {tabs.map((tab) => {
            const isActive = tab.key === 'members';
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  if (tab.key === 'members') return;
                  if (onNavigate) {
                    onNavigate(tab.key);
                  } else {
                    onBack();
                  }
                }}
                className={`pb-3 font-medium transition-colors cursor-pointer relative ${
                  isActive
                    ? 'text-slate-900 font-bold border-b-2 border-sky-600'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-4">
        {/* 2. Top Header with Back, Title & Action Buttons (matching image 9, 10) */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onBack}
              className="w-7 h-7 rounded-full border border-slate-300 hover:border-slate-400 bg-white flex items-center justify-center text-slate-600 hover:text-slate-900 transition-colors cursor-pointer"
              title="ย้อนกลับ"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <h1 className="text-lg sm:text-xl font-bold text-slate-800 tracking-tight">
              ข้อมูลเจ้าของร่วม/สมาชิก
            </h1>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 flex-wrap relative">
            {/* Print / Export CSV dropdown */}
            <div className="relative">
              <div className="inline-flex items-center rounded-lg border border-slate-300 bg-white shadow-2xs">
                <button
                  type="button"
                  onClick={() => handleExportCSV('units')}
                  className="px-3 py-1.5 hover:bg-slate-50 text-slate-700 text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer rounded-l-lg border-r border-slate-200"
                  title="พิมพ์ / ส่งออกไฟล์ CSV (ทะเบียนลูกบ้าน)"
                >
                  <Printer className="w-3.5 h-3.5 text-slate-500" /> พิมพ์
                </button>
                <button
                  type="button"
                  onClick={() => setShowPrintMenu(!showPrintMenu)}
                  className="px-2 py-1.5 hover:bg-slate-50 text-slate-600 text-xs transition-colors cursor-pointer rounded-r-lg"
                  title="ตัวเลือกการพิมพ์และส่งออก CSV"
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </div>

              {showPrintMenu && (
                <div className="absolute right-0 top-full mt-1.5 w-64 bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 z-30 font-prompt animate-in fade-in zoom-in-95 duration-100">
                  <div className="px-3 py-1 text-[11px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">
                    ตัวเลือกการส่งออก / พิมพ์
                  </div>
                  <button
                    type="button"
                    onClick={() => handleExportCSV('units')}
                    className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-sky-50 hover:text-sky-700 flex items-center gap-2 transition-colors cursor-pointer"
                  >
                    <Download className="w-3.5 h-3.5 text-sky-600 shrink-0" />
                    <div>
                      <div className="font-semibold">ดาวน์โหลด CSV (ทะเบียนลูกบ้าน)</div>
                      <div className="text-[10px] text-slate-400">รูปแบบตาราง Excel ตามแบบฟอร์มมาตรฐาน</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleExportCSV('residents')}
                    className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-sky-50 hover:text-sky-700 flex items-center gap-2 transition-colors cursor-pointer"
                  >
                    <Download className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <div>
                      <div className="font-semibold">ดาวน์โหลด CSV (ข้อมูลผู้อยู่อาศัย)</div>
                      <div className="text-[10px] text-slate-400">รายชื่อ, สัญชาติ, เบอร์โทร, การใช้แอปฯ</div>
                    </div>
                  </button>
                  <div className="my-1 border-t border-slate-100" />
                  <button
                    type="button"
                    onClick={() => {
                      setShowPrintMenu(false);
                      window.print();
                    }}
                    className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2 transition-colors cursor-pointer"
                  >
                    <Printer className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                    <span className="font-medium">สั่งพิมพ์ผ่านบราวเซอร์ (Print / PDF)</span>
                  </button>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => setShowGenCodeModal(true)}
              className="px-3.5 py-1.5 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow-2xs transition-colors cursor-pointer"
            >
              <Key className="w-3.5 h-3.5" /> สร้างรหัสสมัครสมาชิก
            </button>
            <button
              type="button"
              onClick={() => setShowAddMemberModal(true)}
              className="px-3.5 py-1.5 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow-2xs transition-colors cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" /> เพิ่มข้อมูลเจ้าของร่วม/สมาชิก
            </button>
          </div>
        </div>

        {/* 3. Status Tabs (ทั้งหมด | อยู่อาศัยเอง | ปล่อยเช่า | ห้องว่าง | ถูกดำเนินการตามกฎหมาย) */}
        <div className="border-b border-slate-200 flex items-center justify-between gap-4 overflow-x-auto text-xs">
          <div className="flex items-center gap-6 min-w-max">
            <button
              type="button"
              onClick={() => setOccupancyTab('all')}
              className={`pb-2.5 font-medium transition-colors cursor-pointer ${
                occupancyTab === 'all'
                  ? 'text-sky-600 font-bold border-b-2 border-sky-600'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              ทั้งหมด
            </button>
            <button
              type="button"
              onClick={() => setOccupancyTab('owner_occupied')}
              className={`pb-2.5 font-medium transition-colors cursor-pointer ${
                occupancyTab === 'owner_occupied'
                  ? 'text-sky-600 font-bold border-b-2 border-sky-600'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              อยู่อาศัยเอง
            </button>
            <button
              type="button"
              onClick={() => setOccupancyTab('rented')}
              className={`pb-2.5 font-medium transition-colors cursor-pointer ${
                occupancyTab === 'rented'
                  ? 'text-sky-600 font-bold border-b-2 border-sky-600'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              ปล่อยเช่า
            </button>
            <button
              type="button"
              onClick={() => setOccupancyTab('vacant')}
              className={`pb-2.5 font-medium transition-colors cursor-pointer ${
                occupancyTab === 'vacant'
                  ? 'text-sky-600 font-bold border-b-2 border-sky-600'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              ห้องว่าง
            </button>
            <button
              type="button"
              onClick={() => setOccupancyTab('legal_action')}
              className={`pb-2.5 font-medium transition-colors cursor-pointer ${
                occupancyTab === 'legal_action'
                  ? 'text-sky-600 font-bold border-b-2 border-sky-600'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              ถูกดำเนินการตามกฎหมาย
            </button>
          </div>
        </div>

        {/* 4. Controls Row: Dropdown, Search Input, and App Usage Indicators */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs pt-1">
          {/* Left: Filter & Search */}
          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={typeDropdownFilter}
              onChange={(e) => setTypeDropdownFilter(e.target.value)}
              className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-slate-700 text-xs focus:outline-none"
            >
              <option value="all">ทั้งหมด</option>
              <option value="บ้านเดี่ยว">บ้านเดี่ยว</option>
              <option value="อาคารพาณิชย์">อาคารพาณิชย์</option>
            </select>

            <div className="relative">
              <input
                type="text"
                placeholder="ค้นหา"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-3 pr-8 py-1.5 bg-white border border-slate-300 rounded-lg text-xs text-slate-700 focus:outline-none w-48 sm:w-60"
              />
              <Search className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2" />
            </div>
          </div>

          {/* Right: App Usage Stats (● ใช้แอปพลิเคชัน 59 หลัง  ● ยังไม่ใช้แอปพลิเคชัน 58 หลัง) */}
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1.5 text-slate-700">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />
              <span>ใช้แอปพลิเคชัน {appActiveCount} หลัง</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-700">
              <span className="w-2.5 h-2.5 rounded-full bg-slate-400 inline-block" />
              <span>ยังไม่ใช้แอปพลิเคชัน {appInactiveCount} หลัง</span>
            </div>
          </div>
        </div>

        {/* 5. Members Data Table (matching Image 9, 10, 22, 23) */}
        <div className="border border-slate-200 rounded-xl overflow-hidden shadow-2xs bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left border-collapse">
              <thead>
                <tr className="bg-slate-100/90 text-slate-700 font-medium border-b border-slate-200">
                  <th className="py-2.5 px-3 text-center whitespace-nowrap">ลำดับ</th>
                  <th className="py-2.5 px-3 whitespace-nowrap">ห้อง</th>
                  <th className="py-2.5 px-3 text-center whitespace-nowrap">ห้องชุดเลขที่</th>
                  <th className="py-2.5 px-4 whitespace-nowrap">เจ้าของกรรมสิทธิ์</th>
                  <th className="py-2.5 px-3 text-center whitespace-nowrap">สัญชาติ</th>
                  <th className="py-2.5 px-3 whitespace-nowrap">ประเภท</th>
                  <th className="py-2.5 px-3 text-right whitespace-nowrap">พื้นที่ (ตร.ว.)</th>
                  <th className="py-2.5 px-3 text-right whitespace-nowrap">%</th>
                  <th className="py-2.5 px-3 text-right whitespace-nowrap">อัตราส่วนกรรมสิทธิ์</th>
                  <th className="py-2.5 px-3 text-center whitespace-nowrap">จำนวนชั้น</th>
                  <th className="py-2.5 px-3 text-center whitespace-nowrap">สถานะ</th>
                  <th className="py-2.5 px-3 text-center whitespace-nowrap">จำนวนผู้อยู่อาศัย</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700">
                {displayedUnits.map((row) => (
                  <tr
                    key={row.id}
                    className="hover:bg-sky-50/40 transition-colors cursor-pointer"
                    onClick={() => setSelectedRoomNumber(row.roomNumber)}
                  >
                    <td className="py-3 px-3 text-center text-slate-500 whitespace-nowrap">
                      {row.orderNo}
                    </td>

                    {/* Room number with app indicator dot */}
                    <td className="py-3 px-3 whitespace-nowrap font-medium">
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`w-2 h-2 rounded-full shrink-0 ${
                            row.usesApp ? 'bg-emerald-500' : 'bg-slate-300'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedRoomNumber(row.roomNumber);
                          }}
                          className="text-sky-600 font-bold hover:underline cursor-pointer"
                        >
                          {row.roomNumber}
                        </button>
                      </div>
                    </td>

                    <td className="py-3 px-3 text-center text-slate-500 whitespace-nowrap">
                      {row.unitDeedNumber}
                    </td>

                    <td className="py-3 px-4 font-medium text-slate-800 whitespace-nowrap">
                      {row.ownerName}
                    </td>

                    <td className="py-3 px-3 text-center text-slate-700 font-medium whitespace-nowrap">
                      {row.nationality}
                    </td>

                    <td className="py-3 px-3 text-slate-600 whitespace-nowrap">
                      {row.propertyType}
                    </td>

                    <td className="py-3 px-3 text-right text-slate-700 whitespace-nowrap">
                      {row.areaSqw.toFixed(2)}
                    </td>

                    <td className="py-3 px-3 text-right text-slate-700 whitespace-nowrap">
                      {row.ownershipPercent}
                    </td>

                    <td className="py-3 px-3 text-right text-slate-700 whitespace-nowrap">
                      {row.ownershipRatio}
                    </td>

                    <td className="py-3 px-3 text-center text-slate-700 whitespace-nowrap">
                      {row.floors}
                    </td>

                    <td className="py-3 px-3 text-center whitespace-nowrap">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-[11px] font-semibold ${
                          row.status === 'ปล่อยเช่า'
                            ? 'bg-sky-50 text-sky-700 border border-sky-200'
                            : row.status === 'อยู่อาศัยเอง'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : row.status === 'ห้องว่าง'
                            ? 'bg-slate-100 text-slate-600'
                            : 'bg-rose-50 text-rose-700 border border-rose-200'
                        }`}
                      >
                        {row.status}
                      </span>
                    </td>

                    <td className="py-3 px-3 text-center whitespace-nowrap">
                      <span className="w-5 h-5 rounded-full bg-sky-500 text-white font-bold text-[10px] inline-flex items-center justify-center">
                        {row.residentCount}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Table Footer with Pagination matching screenshot */}
          <div className="bg-white px-4 py-3 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
            <div className="flex items-center gap-2">
              <span>แสดง {rowsPerPage} รายการ/หน้า</span>
            </div>

            <div className="flex items-center gap-1">
              <span>รายการทั้งหมด {totalItems}</span>
              <div className="flex items-center gap-1 ml-4">
                <button
                  type="button"
                  onClick={() => setCurrentPage(1)}
                  className={`px-2 py-0.5 rounded border ${
                    currentPage === 1
                      ? 'border-sky-600 bg-sky-50 text-sky-700 font-bold'
                      : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  1
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage(2)}
                  className={`px-2 py-0.5 rounded border ${
                    currentPage === 2
                      ? 'border-sky-600 bg-sky-50 text-sky-700 font-bold'
                      : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  2
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage(3)}
                  className="px-2 py-0.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  3
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage(4)}
                  className="px-2 py-0.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  4
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage(5)}
                  className="px-2 py-0.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  5
                </button>
                <span className="px-1">...</span>
                <button
                  type="button"
                  onClick={() => setCurrentPage(12)}
                  className="px-2 py-0.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  12
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal: เพิ่มข้อมูลเจ้าของร่วม/สมาชิก */}
      {showAddMemberModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl border border-slate-200 p-6 space-y-4 animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-800 text-sm">เพิ่มข้อมูลเจ้าของร่วม/สมาชิก</h3>
              <button
                type="button"
                onClick={() => setShowAddMemberModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-600 font-semibold mb-1">เลขที่ห้อง *</label>
                  <input
                    type="text"
                    placeholder="เช่น 1086/15"
                    className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-semibold mb-1">ห้องชุดเลขที่</label>
                  <input
                    type="text"
                    placeholder="เช่น A-105"
                    className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-600 font-semibold mb-1">
                  ชื่อเจ้าของกรรมสิทธิ์ *
                </label>
                <input
                  type="text"
                  placeholder="ชื่อ-นามสกุล"
                  className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-600 font-semibold mb-1">ประเภท</label>
                  <select className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs">
                    <option>บ้านเดี่ยว</option>
                    <option>ทาวน์โฮม</option>
                    <option>อาคารพาณิชย์</option>
                  </select>
                </div>
                <div>
                  <label className="block text-slate-600 font-semibold mb-1">สัญชาติ</label>
                  <select className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs">
                    <option>TH</option>
                    <option>AF</option>
                    <option>US</option>
                    <option>CH</option>
                    <option>SE</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-slate-600 font-semibold mb-1">พื้นที่ (ตร.ว.)</label>
                  <input
                    type="number"
                    placeholder="120.0"
                    className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-semibold mb-1">อัตราส่วน</label>
                  <input
                    type="text"
                    placeholder="1400.0"
                    className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-semibold mb-1">สถานะ</label>
                  <select className="w-full p-2 bg-white border border-slate-300 rounded-lg text-xs">
                    <option>อยู่อาศัยเอง</option>
                    <option>ปล่อยเช่า</option>
                    <option>ห้องว่าง</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowAddMemberModal(false)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium text-xs transition-colors"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                onClick={() => setShowAddMemberModal(false)}
                className="px-5 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-xl font-bold text-xs transition-colors"
              >
                บันทึกข้อมูล
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal / View: สร้างรหัสสมัครสมาชิก - แสดงไฟล์พร้อมปริ้น A4 สำหรับทุกห้อง/บ้าน (100 หลัง/ห้อง) */}
      {showGenCodeModal && (
        <MemberPasscodePrintModal
          onClose={() => setShowGenCodeModal(false)}
          unitsCount={100}
        />
      )}
      {/* Floating Toast Notification */}
      {showPrintSuccessToast && (
        <div className="fixed bottom-6 right-6 z-50 bg-slate-900 text-white px-4 py-3 rounded-xl shadow-2xl flex items-center gap-3 border border-slate-700 animate-in fade-in slide-in-from-bottom-5 duration-200">
          <div className="w-7 h-7 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-4 h-4" />
          </div>
          <div>
            <div className="text-xs font-semibold text-white">ส่งออกข้อมูลสำเร็จ</div>
            <div className="text-[11px] text-slate-300">{toastMessage}</div>
          </div>
          <button
            type="button"
            onClick={() => setShowPrintSuccessToast(false)}
            className="text-slate-400 hover:text-white p-1 ml-2 transition-colors cursor-pointer"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};
